// Kiriman. Concept lines (JEV ui.region_gate, all kept):
//   sub_header   Job: name the screen and how many submissions match.
//                Message: Kiriman, n matching. Action: none. Container:
//                plain spacing.
//   sub_counts   Job: say how many submissions have come in. Message: Total
//                masuk (all time), Hari ini, and Cocok filter only while a
//                filter is applied. Action: none. Container: one hairline
//                strip split by column rules (JEV ui.component_recipe
//                count_block hairline_strip, conf 1.0); mono figures, no teal.
//                Polls with the list; a new arrival in either refetches both.
//   sub_toolbar  Job: narrow the list and take it out as CSV. Message: search
//                by name or NIK, filter by phone and dates, export the
//                filtered set. Action: Ekspor CSV. Container: card, recipe
//                core.field_row_card (labels above, one row from 1024).
//   sub_table    Job: scan submissions, reveal one row's full NIK and phone,
//                page through. Message: Waktu, Nama, NIK, Nomor, Pengirim,
//                masked by default. Action: Tampilkan per row, prev and next.
//                Container: rows, recipe core.table (Carbon DataTable from
//                1024 with column rules on one fixed column grid shared with
//                Ringkasan, stacked records below 1024, the page never
//                scrolls sideways). Signature: Tampilkan swaps the masked NIK
//                and phone for the full values in place, the row takes a
//                quiet tonal tint (JEV ui.component_recipe outline_toggle).
//                Refreshes every 10 s while visible.
// Hand-written inside the kit Section: the kit has no DataTable, filter
// toolbar, or pagination composition.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Section } from "@bot-whatsapp/ui";
import { ApiError, isUnauthorized, useDashboardApi } from "./api";
import { useDelayed, useOnUnauthorized, useResource } from "./data";
import { formatNumber } from "./format";
import { EkstraksiDialog } from "./Ekstraksi";
import { useReveal } from "./reveal";
import type { Submission, SubmissionFilters } from "./types";
import {
  ConfirmDialog,
  CountStrip,
  EmptyState,
  LoadError,
  Notice,
  RowsSkeleton,
  ScreenHeader,
  SubmissionRecords,
  type ConfirmRequest,
} from "./ui";

export const PAGE_SIZE = 50;
export const POLL_MS = 10_000;

/** The value 300 ms after typing stops; flush() applies it at once (Enter). */
function useDebounced<T>(value: T, ms = 300): [T, () => void] {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return [settled, () => setSettled(value)];
}

/** Enter in a filter field applies it now instead of after the debounce. */
function onEnter(run: () => void) {
  return (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      run();
    }
  };
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function SubmissionsScreen() {
  const api = useDashboardApi();
  const headingId = useId();
  const tableCaptionId = useId();
  const extraId = useId();
  const [detailId, setDetailId] = useState<string | null>(null);
  const ids = { q: useId(), phone: useId(), from: useId(), to: useId() };

  const [q, setQ] = useState("");
  const [phone, setPhone] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [moreOpen, setMoreOpen] = useState(false);
  const reveal = useReveal((id) => api.revealSubmission(id));
  const onUnauthorized = useOnUnauthorized();
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  const [dq, flushQ] = useDebounced(q.trim());
  const [dphone, flushPhone] = useDebounced(phone.trim());
  const filters: SubmissionFilters = {
    q: dq || undefined,
    phone: dphone || undefined,
    from: from || undefined,
    to: to || undefined,
  };
  const key = JSON.stringify(filters);

  // A new query always starts at page 1.
  useEffect(() => setPage(1), [key]);

  const list = useResource(() => api.listSubmissions({ ...filters, page, pageSize: PAGE_SIZE }), [api, key, page], { pollMs: POLL_MS });
  const overview = useResource(() => api.overview(), [api], { pollMs: POLL_MS });
  const data = list.data;
  const slow = useDelayed(list.loading && !data);
  const applied = Boolean(filters.q || filters.phone || filters.from || filters.to);

  // The two polls are not in step, so a new arrival seen by one refetches
  // the other: the overview total grew, or the list's newest row changed
  // under the same query and page.
  const { reload: reloadList } = list;
  const { reload: reloadOverview } = overview;
  const seenTotal = overview.data?.submissionsTotal;
  const lastTotal = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (seenTotal === undefined) return;
    if (lastTotal.current !== undefined && lastTotal.current !== seenTotal) reloadList();
    lastTotal.current = seenTotal;
  }, [seenTotal, reloadList]);
  const newest = data && data.items[0] ? `${key}|${page}|${data.items[0].id}` : undefined;
  const lastNewest = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (newest === undefined) return;
    const prev = lastNewest.current;
    const sameQuery = prev !== undefined && prev.slice(0, prev.lastIndexOf("|")) === newest.slice(0, newest.lastIndexOf("|"));
    if (sameQuery && prev !== newest) reloadOverview();
    lastNewest.current = newest;
  }, [newest, reloadOverview]);
  const extraCount = [phone, from, to].filter(Boolean).length;
  const anyFilter = Boolean(q || extraCount);

  function clearFilters() {
    setQ("");
    setPhone("");
    setFrom("");
    setTo("");
  }

  async function exportCsv() {
    if (exporting) return;
    setExporting(true);
    setExportFailed(false);
    try {
      const { blob, filename } = await api.exportCsv(filters);
      saveBlob(blob, filename);
    } catch {
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  }

  const first = data && data.total > 0 ? (data.page - 1) * data.pageSize + 1 : 0;
  const last = data ? Math.min(data.page * data.pageSize, data.total) : 0;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  // The counts live in the strip below; the meta line never repeats them.
  const meta = applied
    ? "Menampilkan kiriman yang cocok dengan filter. NIK dan nomor disamarkan sampai Anda menampilkannya."
    : "NIK dan nomor disamarkan sampai Anda menampilkannya.";

  const counts = [
    { id: "total", label: "Total masuk", value: overview.data?.submissionsTotal },
    { id: "today", label: "Hari ini", value: overview.data?.submissionsToday },
    ...(applied ? [{ id: "match", label: "Cocok filter", value: data?.total }] : []),
  ];

  function askDelete(s: Submission) {
    setConfirmError(null);
    setConfirm({
      title: `Hapus kiriman ${s.name}?`,
      body: `Data ${s.name} dengan NIK ${s.nikMasked} dihapus permanen dan tidak bisa dikembalikan. Penghapusan dicatat di log akses.`,
      confirmLabel: "Hapus permanen",
      run: () => api.deleteSubmission(s.id).then(() => undefined),
    });
  }

  async function runConfirm() {
    if (!confirm || confirmBusy) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await confirm.run();
      setConfirm(null);
      list.reload();
      overview.reload();
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      // Already gone (another operator deleted it): refresh instead of failing.
      if (err instanceof ApiError && err.status === 404) {
        setConfirm(null);
        list.reload();
        overview.reload();
      } else {
        setConfirmError("Data belum terhapus. Coba lagi sebentar lagi.");
      }
    } finally {
      setConfirmBusy(false);
    }
  }

  return (
    <Section id="kiriman" className="dash-screen" composition="custom" variant="records" labelledBy={headingId}>
      <ScreenHeader id={headingId} title="Kiriman" meta={meta} />

      <CountStrip label="Jumlah kiriman" items={counts} />

      <div className="dash-card dash-toolbar kit-full">
        <div className="dash-filter-grid" id={extraId} data-open={moreOpen || undefined}>
          <div className="field dash-filter-search">
            <label htmlFor={ids.q}>Cari nama atau NIK</label>
            <input
              id={ids.q}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onEnter(flushQ)}
              enterKeyHint="search"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="field dash-filter-extra dash-filter-phone">
            <label htmlFor={ids.phone}>Nomor pengirim</label>
            <input
              id={ids.phone}
              type="tel"
              onKeyDown={onEnter(flushPhone)}
              enterKeyHint="search"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="field dash-filter-extra">
            <label htmlFor={ids.from}>Dari tanggal</label>
            <input id={ids.from} type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field dash-filter-extra">
            <label htmlFor={ids.to}>Sampai tanggal</label>
            <input id={ids.to} type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <div className="dash-actions">
          <button
            type="button"
            className="btn-secondary dash-more"
            aria-expanded={moreOpen}
            aria-controls={extraId}
            onClick={() => setMoreOpen((o) => !o)}
          >
            {extraCount > 0 ? `Filter lain (${extraCount})` : "Filter lain"}
          </button>
          <button type="button" className="btn-secondary" onClick={exportCsv} aria-busy={exporting || undefined}>
            Ekspor CSV
          </button>
          {anyFilter ? (
            <button type="button" className="btn-ghost" onClick={clearFilters}>
              Hapus filter
            </button>
          ) : null}
        </div>
        {exportFailed ? (
          <Notice
            tone="danger"
            title="Ekspor gagal"
            action={
              <button type="button" className="btn-secondary" onClick={exportCsv}>
                Coba lagi
              </button>
            }
          >
            File CSV belum dibuat. Periksa koneksi ke server, lalu coba lagi.
          </Notice>
        ) : null}
      </div>

      <div className="dash-records kit-full" aria-busy={list.loading || undefined}>
        {list.error && !data ? (
          <LoadError what="Kiriman" onRetry={list.reload} />
        ) : !data ? (
          slow ? <RowsSkeleton rows={6} /> : null
        ) : data.items.length === 0 ? (
          anyFilter ? (
            <EmptyState
              title="Tidak ada kiriman yang cocok"
              body="Ubah kata kunci atau rentang tanggal, atau hapus semua filter untuk melihat semua kiriman."
              action={
                <button type="button" className="btn-secondary" onClick={clearFilters}>
                  Hapus filter
                </button>
              }
            />
          ) : (
            <EmptyState
              title="Belum ada kiriman"
              body="Kiriman muncul di sini setelah pengirim yang terpasang mengirim nama dan NIK ke bot."
              action={
                <a className="btn btn-secondary" href="#kode">
                  Buat kode pairing
                </a>
              }
            />
          )
        ) : (
          <>
            <SubmissionRecords items={data.items} describedBy={tableCaptionId} reveal={reveal} onDelete={askDelete} onDetail={(row) => setDetailId(row.id)} />

            <nav className="dash-pager" aria-label="Halaman kiriman">
              <p id={tableCaptionId} className="kit-meta">
                Menampilkan <span className="kit-num">{formatNumber(first)}</span> sampai{" "}
                <span className="kit-num">{formatNumber(last)}</span> dari <span className="kit-num">{formatNumber(data.total)}</span>{" "}
                kiriman
              </p>
              {pages > 1 ? (
              <div className="dash-pager-buttons">
                <button type="button" className="btn-secondary" disabled={data.page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Sebelumnya
                </button>
                <button type="button" className="btn-secondary" disabled={data.page >= pages} onClick={() => setPage((p) => p + 1)}>
                  Berikutnya
                </button>
              </div>
              ) : null}
            </nav>
          </>
        )}
      </div>
      <EkstraksiDialog id={detailId} onClose={() => setDetailId(null)} />
      <ConfirmDialog
        request={confirm}
        busy={confirmBusy}
        error={confirmError}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      />
    </Section>
  );
}
